<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { flatten } from 'flat'
import { capitalize, get, isEmpty } from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { format as formatDate, formatDistanceToNow } from 'date-fns'
import { Columns3 } from 'lucide-vue-next'
import { useCan } from '@/modules/auth/composables/use-can'
import {
  VButton,
  VDataTable,
  VDropdownImproved,
  VIcon,
  VSearchInput,
  VSection,
  VStats,
} from '@/modules/shared/components'
import type { Stat } from '@/modules/shared/components/types'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { search as deepSearch } from '@/modules/shared/utils/deep-search'
import { export_csv } from '@/modules/shared/utils/export-csv'
import { useEntityStore } from '../stores/entity-store'
import EntitiesNav from '../components/entities-nav.vue'
import LifecycleBadge from '../components/lifecycle-badge.vue'
import LifecyclePill from '../components/lifecycle-pill.vue'
import PaymentStatusCell from '../components/payment-status-cell.vue'
import { addressColumns } from '../config/data-table-columns'
import { useAuthStore } from '../../auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'
import { investorPath } from '../utils/investor'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type EntityType = 'fund' | 'spv' | 'gp'
type LifecycleState = 'raising' | 'closed' | 'exited'
type LifecycleFilter = 'all' | LifecycleState

interface MoneyValue {
  amount: number | string
  common_amount?: number | string
  currency: string
  common_currency?: string
  date: null
}

///////////////////////////////////////////////////////////////////////////////
// Utils
///////////////////////////////////////////////////////////////////////////////

const formatEntityType = (type: string) => {
  const entity_types: Record<string, string> = { fund: 'Fund', spv: 'SPV', gp: 'GP' }
  return entity_types[type] ?? type
}

const amountOf = (value: unknown): number => {
  if (value && typeof value === 'object' && 'amount' in (value as MoneyValue)) {
    return Number((value as MoneyValue).amount) || 0
  }
  return 0
}

const sumCurrency = (items: any[], key: string): MoneyValue => {
  let amount = 0
  let common_amount = 0
  let currency = ''
  let common_currency = ''
  for (const item of items ?? []) {
    const v = item?.[key] as MoneyValue | undefined
    if (!v) continue
    amount += Number(v.amount ?? 0)
    common_amount += Number(v.common_amount ?? v.amount ?? 0)
    currency ||= v.currency ?? ''
    common_currency ||= v.common_currency ?? v.currency ?? ''
  }
  return {
    amount,
    common_amount,
    currency: currency || 'USD',
    common_currency: common_currency || currency || 'USD',
    date: null,
  }
}

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { can } = useCan()
const { n, t } = useExtendedI18n()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const { hidden } = useHiddenMetrics()
const route = useRoute()

const cid = computed(() => `${route.params.entity_type}:${route.params.entity_id}`)
const isAdmin = computed(() => authStore.is_site_or_group_admin)
const entity_type = (route.params.entity_type as string).slice(0, -1) as EntityType

const entityStore = useEntityStore()
const entities = computed<any[]>(() => entityStore[route.params.entity_type as string] ?? [])
const skeleton = ref(true)

///////////////////////////////////////////////////////////////////////////////
// Columns (VDataTable shape; `name` kept for export_csv back-compat)
///////////////////////////////////////////////////////////////////////////////

const baseColumns = computed(() => {
  const isCanAdmin = can('admin', cid.value)
  const showSpvManager = entity_type === 'spv'
  const cashAccessible = !hidden.value.cashPosition

  const cols = [
    {
      key: 'lifecycle_state',
      name: 'Status',
      label: 'Status',
      type: 'string' as const,
      align: 'left' as const,
      aggregate: null,
    },
    {
      key: 'name',
      name: capitalize(t('shared.name')),
      label: capitalize(t('shared.name')),
      type: 'string' as const,
      align: 'left' as const,
    },
    {
      key: 'investor_count',
      name: capitalize(t('shared.investor', 0)),
      label: capitalize(t('shared.investor', 0)),
      type: 'integer' as const,
      align: 'right' as const,
      hidden: true,
    },
    {
      key: 'committed',
      name: capitalize(t('shared.committed')),
      label: capitalize(t('shared.committed')),
      type: 'currency' as const,
      aggregate: (items: any[]) => sumCurrency(items, 'committed'),
      flatten: (item: any) => item.committed,
    },
    {
      key: 'called',
      name: capitalize(t('shared.called')),
      label: capitalize(t('shared.called')),
      type: 'currency' as const,
      aggregate: (items: any[]) => sumCurrency(items, 'called'),
      flatten: (item: any) => item.called,
    },
    {
      key: 'commitment_remaining',
      name: capitalize(t('shared.commitment remaining')),
      label: capitalize(t('shared.commitment remaining')),
      type: 'currency' as const,
      hidden: true,
      aggregate: (items: any[]) => sumCurrency(items, 'commitment_remaining'),
      flatten: (item: any) => item.commitment_remaining,
    },
    {
      key: 'flex_or_traditional',
      name: capitalize(t('shared.flex or traditional')),
      label: capitalize(t('shared.flex or traditional')),
      type: 'string' as const,
      align: 'right' as const,
      hidden: true,
    },
    {
      key: 'distributed',
      name: capitalize(t('shared.distributed')),
      label: capitalize(t('shared.distributed')),
      type: 'currency' as const,
      aggregate: (items: any[]) => sumCurrency(items, 'distributed'),
      flatten: (item: any) => item.distributed,
    },
    cashAccessible && {
      key: 'cash_position',
      name: capitalize(t('shared.cash position')),
      label: capitalize(t('shared.cash position')),
      type: 'currency' as const,
      aggregate: (items: any[]) => sumCurrency(items, 'cash_position'),
      flatten: (item: any) => item.cash_position,
    },
    {
      key: 'management_fee',
      name: capitalize(t('shared.management fee', 0)),
      label: capitalize(t('shared.management fee', 0)),
      type: 'currency' as const,
      hidden: true,
      aggregate: (items: any[]) => sumCurrency(items, 'management_fee'),
      flatten: (item: any) => item.management_fee,
    },
    {
      key: 'other_fees',
      name: capitalize(t('shared.other fee', 0)),
      label: capitalize(t('shared.other fee', 0)),
      type: 'currency' as const,
      hidden: true,
      aggregate: (items: any[]) => sumCurrency(items, 'other_fees'),
      flatten: (item: any) => item.other_fees,
    },
    {
      key: 'carried_interest_distributed',
      name: capitalize(t('shared.carried interest')),
      label: capitalize(t('shared.carried interest')),
      type: 'currency' as const,
      hidden: true,
      aggregate: (items: any[]) => sumCurrency(items, 'carried_interest_distributed'),
      flatten: (item: any) => item.carried_interest_distributed,
    },
    {
      key: 'legal_name',
      name: capitalize(t('shared.legal name')),
      label: capitalize(t('shared.legal name')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    {
      key: 'date_established',
      name: capitalize(t('shared.date established')),
      label: capitalize(t('shared.date established')),
      type: 'date' as const,
      align: 'right' as const,
    },
    {
      key: 'group_name',
      name: capitalize(t('shared.group')),
      label: capitalize(t('shared.group')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
      flatten: (item: any) => item.group?.name,
    },
    isCanAdmin && {
      key: 'group_subdomain',
      name: capitalize(t('shared.subdomain')),
      label: capitalize(t('shared.subdomain')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
      flatten: (item: any) => item.group?.subdomain,
    },
    {
      key: 'type',
      name: capitalize(t('shared.legal structure')),
      label: capitalize(t('shared.legal structure')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    isCanAdmin && {
      key: 'tax_id',
      name: capitalize(t('shared.EIN')),
      label: capitalize(t('shared.EIN')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    isCanAdmin && {
      key: 'document_status',
      name: capitalize(t('shared.document status')),
      label: capitalize(t('shared.document status')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    isCanAdmin && {
      key: 'selected_subscriptions',
      name: capitalize(t('shared.subscription')),
      label: capitalize(t('shared.subscription')),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    showSpvManager && {
      key: 'manager',
      name: 'SPV Manager',
      label: 'SPV Manager',
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
      flatten: (item: any) => item.manager?.name,
    },
    {
      key: 'managers',
      name: capitalize(t('shared.manager', 0)),
      label: capitalize(t('shared.manager', 0)),
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    {
      key: 'payment_status',
      name: 'Payment status',
      label: 'Payment status',
      type: 'string' as const,
      align: 'left' as const,
    },
    ...addressColumns(),
  ]

  return cols.filter(Boolean) as any[]
})

///////////////////////////////////////////////////////////////////////////////
// Column visibility (user-toggleable, outside the table)
///////////////////////////////////////////////////////////////////////////////

const defaultVisibleKeys = computed(() => baseColumns.value.filter((c: any) => !c.hidden).map((c: any) => c.key))

const visibleColumnKeys = ref<string[]>([...defaultVisibleKeys.value])

watch(defaultVisibleKeys, (keys) => {
  const existing = new Set(visibleColumnKeys.value)
  for (const k of keys) if (!existing.has(k)) visibleColumnKeys.value.push(k)
})

const columns = computed(() =>
  baseColumns.value.map((col: any) => ({
    ...col,
    // Pinned columns are always visible regardless of the user's toggle state.
    hidden: PINNED_KEYS.has(col.key) ? false : !visibleColumnKeys.value.includes(col.key),
  })),
)

// Status + Name are always visible (they're the sticky/pinned columns); hide
// them from the column-toggle dropdown so users can't break the layout.
const PINNED_KEYS = new Set(['lifecycle_state', 'name'])

const columnOptions = computed(() =>
  baseColumns.value
    .filter((col: any) => !PINNED_KEYS.has(col.key))
    .map((col: any) => ({ value: col.key, label: col.label }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label)),
)

// Grid template: fixed widths for the pinned columns + one minmax(8rem,1fr)
// per remaining visible column. Dynamic count fills the table width instead
// of leaving empty tracks on the right.
const restColumnCount = computed(
  () => baseColumns.value.filter((c: any) => !PINNED_KEYS.has(c.key) && visibleColumnKeys.value.includes(c.key)).length,
)

const gridTemplateColumns = computed(() => {
  // max-content minimum lets each track size to its widest cell so content
  // never overflows into the next column. Combined with `overflow: auto` on
  // .vtable, excess width triggers horizontal scroll instead of overlap.
  const rest = 'minmax(max-content, 1fr) '.repeat(restColumnCount.value).trim()
  return `0 9rem 18rem ${rest}`.trim()
})

///////////////////////////////////////////////////////////////////////////////
// Capital Flow header (computed across ALL entities, not filtered)
///////////////////////////////////////////////////////////////////////////////

const committedTotal = computed(() => sumCurrency(entities.value, 'committed'))
const calledTotal = computed(() => sumCurrency(entities.value, 'called'))
const distributedTotal = computed(() => sumCurrency(entities.value, 'distributed'))
const cashPositionTotal = computed(() => sumCurrency(entities.value, 'cash_position'))

const calledPct = computed(() => {
  const c = amountOf(committedTotal.value)
  return c > 0 ? Math.round((amountOf(calledTotal.value) / c) * 100) : 0
})

const moic = computed(() => {
  const c = amountOf(calledTotal.value)
  return c > 0 ? amountOf(distributedTotal.value) / c : 0
})

const deployedPct = computed(() => {
  const c = amountOf(calledTotal.value)
  return c > 0 ? Math.round(((c - amountOf(cashPositionTotal.value)) / c) * 100) : 0
})

const targetRaiseTotal = computed(() =>
  entities.value.reduce((sum: number, entity: any) => sum + amountOf(entity?.deal_card?.target_raise), 0),
)

const goalPct = computed(() => {
  const target = targetRaiseTotal.value
  return target > 0 ? Math.round((amountOf(committedTotal.value) / target) * 100) : null
})

const entityCountLabel = computed(() => {
  const count = entities.value.length
  return `${count} ${formatEntityType(entity_type)}${count === 1 ? '' : 's'}`
})

const kpiStats = computed<Stat[]>(() => [
  {
    label: 'Committed',
    value: committedTotal.value,
    type: 'currency',
    useCommonCurrency: true,
    hideSign: true,
    sublabel: goalPct.value !== null ? `${goalPct.value}% of goal` : `across ${entityCountLabel.value}`,
  },
  {
    label: 'Called',
    value: calledTotal.value,
    type: 'currency',
    useCommonCurrency: true,
    hideSign: true,
    sublabel: `${calledPct.value}% called`,
  },
  {
    label: 'Distributed',
    value: distributedTotal.value,
    type: 'currency',
    useCommonCurrency: true,
    hideSign: true,
    sublabel: `MOIC ${moic.value.toFixed(2)}x`,
  },
  {
    label: 'Cash Position',
    value: cashPositionTotal.value,
    type: 'currency',
    useCommonCurrency: true,
    sublabel: `${deployedPct.value}% Deployed`,
    visible: !hidden.value.cashPosition,
  },
])

///////////////////////////////////////////////////////////////////////////////
// Page header
///////////////////////////////////////////////////////////////////////////////

const pageTitle = computed(() => `${formatEntityType(entity_type)}s`)

const onExport = () => {
  const filename = `${route.params.slug}-${route.params.entity_type}`
  export_csv(filename, displayedEntities.value, baseColumns.value, n)
}

///////////////////////////////////////////////////////////////////////////////
// Filters (lifecycle pills + search) — applied outside the table
///////////////////////////////////////////////////////////////////////////////

const searchQuery = ref('')
const lifecycleFilter = ref<LifecycleFilter>('all')

const lifecyclePills = computed<{ key: LifecycleFilter; label: string }[]>(() => {
  const pills: { key: LifecycleFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'raising', label: 'Raising' },
    { key: 'closed', label: 'Closed' },
  ]
  // Funds can't be exited (rejected at validation in Lifecycleable); hide the pill.
  if (entity_type !== 'fund') pills.push({ key: 'exited', label: 'Exited' })
  return pills
})

const statusCounts = computed(() => {
  const all = entities.value.length
  let raising = 0
  let closed = 0
  let exited = 0
  for (const row of entities.value) {
    if (row.lifecycle_state === 'raising') raising++
    else if (row.lifecycle_state === 'closed') closed++
    else if (row.lifecycle_state === 'exited') exited++
  }
  return { all, raising, closed, exited }
})

const displayedEntities = computed(() => {
  let items = entities.value
  if (lifecycleFilter.value !== 'all') {
    items = items.filter((e: any) => e.lifecycle_state === lifecycleFilter.value)
  }
  const q = searchQuery.value.trim()
  if (q) {
    items = deepSearch(items.map((e: any) => flatten(e)) as any[], q) as any[]
  }
  return items.map((item: any) => ({
    ...item,
    accent_color:
      item.lifecycle_state === 'raising'
        ? '#059669'
        : item.lifecycle_state === 'closed'
          ? '#3092b3'
          : item.lifecycle_state === 'exited'
            ? '#246586'
            : undefined,
  }))
})

///////////////////////////////////////////////////////////////////////////////
// Per-row formatters / badges
///////////////////////////////////////////////////////////////////////////////

const pctOfGoal = (row: any): string => {
  const target = Number(row?.deal_card?.target_raise?.amount ?? 0)
  const committed = Number(row?.committed?.amount ?? 0)
  if (target <= 0) return '0% of goal'
  return `${Math.round((committed / target) * 100)}% of goal`
}

const pctCalled = (row: any): string => {
  const committed = Number(row?.committed?.amount ?? 0)
  const called = Number(row?.called?.amount ?? 0)
  const pct = committed > 0 ? Math.round((called / committed) * 100) : 0
  return `${pct}% called`
}

const moicLabel = (row: any): string => {
  const called = Number(row?.called?.amount ?? 0)
  const distributed = Number(row?.distributed?.amount ?? 0)
  const value = called > 0 ? distributed / called : 0
  return `MOIC ${value.toFixed(2)}x`
}

const pctDeployed = (row: any): string => {
  const called = Number(row?.called?.amount ?? 0)
  const cash = Number(row?.cash_position?.amount ?? 0)
  const pct = called > 0 ? Math.round(((called - cash) / called) * 100) : 0
  return `${pct}% Deployed`
}

const relativeDate = (raw: any): string => {
  if (!raw) return ''
  try {
    const d = new Date(raw)
    return formatDistanceToNow(d, { addSuffix: true })
  } catch {
    return ''
  }
}

const absoluteDate = (raw: any): string => {
  if (!raw) return ''
  try {
    return formatDate(new Date(raw), 'MMM d, yyyy')
  } catch {
    return ''
  }
}

onMounted(async () => {
  // prettier-ignore
  await Promise.all([
    workspaceStore.fetchCurrent(),
    entityStore.fetchEntitiesV3(entity_type),
  ])

  skeleton.value = false
})
</script>

<template>
  <TheLayout>
    <div class="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end sm:gap-4">
      <div class="min-w-0">
        <h1 class="text-2xl font-semibold text-gray-900 sm:text-3xl">{{ pageTitle }}</h1>
      </div>
      <div class="flex w-full items-center gap-2 sm:w-auto sm:gap-3">
        <VButton size="md" variant="secondary" @click="onExport" class="flex-1 sm:flex-none">
          <div class="flex items-center justify-center space-x-2">
            <VIcon name="Download" class="h-4 w-4" />
            <span>Export</span>
          </div>
        </VButton>
        <RouterLink v-if="can('admin', cid)" :to="{ name: `investing.${entity_type}-new` }" class="flex-1 sm:flex-none">
          <VButton size="md" variant="v-blue" class="w-full sm:w-auto">
            <div class="flex items-center justify-center space-x-2">
              <VIcon name="Plus" />
              <span>Add {{ formatEntityType(entity_type) }}</span>
            </div>
          </VButton>
        </RouterLink>
      </div>
    </div>

    <VSection>
      <EntitiesNav v-if="entity_type !== 'spv'" />

      <VStats :skeleton="skeleton" :stats="kpiStats" :precision="0" dark class="mb-4" />

      <div
        class="entities-card overflow-hidden rounded-lg bg-white [box-shadow:var(--shadow-ring),var(--shadow-elevation)]"
        :style="{ '--grid-cols': gridTemplateColumns }"
      >
        <!-- Toolbar: pills LEFT, search + funnel + columns RIGHT -->
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div class="flex flex-wrap items-center gap-2">
            <LifecyclePill
              v-for="pill in lifecyclePills"
              :key="pill.key"
              :filter-key="pill.key"
              :label="pill.label"
              :count="statusCounts[pill.key]"
              :active="lifecycleFilter === pill.key"
              @select="lifecycleFilter = pill.key"
            />
          </div>
          <div class="flex items-center gap-2">
            <VSearchInput v-model="searchQuery" :placeholder="`Search ${pageTitle}...`" />
            <VDropdownImproved
              v-model="visibleColumnKeys"
              :options="columnOptions"
              aligned="right"
              :hideDisabled="true"
            >
              <button
                type="button"
                class="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 hover:bg-gray-50"
              >
                <Columns3 class="h-4 w-4" />
              </button>
            </VDropdownImproved>
          </div>
        </div>

        <VDataTable
          :columns="columns"
          :items="displayedEntities"
          :loading="skeleton"
          :sortBy="[{ key: 'name', direction: 'asc' }]"
          empty-title="No entities to display"
          empty-message="Try adjusting filters or search to see results."
        >
          <template #lifecycle_state="{ item }">
            <LifecycleBadge :status="item.lifecycle_state" />
          </template>

          <template #name="{ item }">
            <div class="min-w-0 max-w-full">
              <RouterLink
                class="block truncate font-semibold text-[#3b88af] hover:underline"
                :to="{
                  name: 'investing.entity-overview',
                  params: { entity_id: item.id, entity_type: item.entity_type },
                }"
                :title="item.name"
              >
                {{ item.name }}
              </RouterLink>
              <div
                v-if="!isEmpty(item.legal_name)"
                class="mt-0.5 truncate text-xs text-gray-500"
                :title="item.legal_name"
              >
                {{ item.legal_name }}
              </div>
            </div>
          </template>

          <template #committed="{ item }">
            <div class="text-right">
              <div class="font-semibold text-gray-900">{{ n(item.committed, 'currency') }}</div>
              <div class="mt-0.5 text-xs text-gray-500">
                {{ pctOfGoal(item) }}
              </div>
            </div>
          </template>

          <template #called="{ item }">
            <div class="text-right">
              <div class="font-semibold text-gray-900">{{ n(item.called, 'currency') }}</div>
              <div class="mt-0.5 text-xs text-gray-500">{{ pctCalled(item) }}</div>
            </div>
          </template>

          <template #distributed="{ item }">
            <div class="text-right">
              <div class="font-semibold text-gray-900">{{ n(item.distributed, 'currency') }}</div>
              <div class="mt-0.5 text-xs text-gray-500">{{ moicLabel(item) }}</div>
            </div>
          </template>

          <template #cash_position="{ item }">
            <div class="text-right">
              <div class="font-semibold text-gray-900">{{ n(item.cash_position, 'currency') }}</div>
              <div class="mt-0.5 text-xs text-gray-500">{{ pctDeployed(item) }}</div>
            </div>
          </template>

          <template #commitment_remaining="{ item }">
            <div class="text-right text-gray-900">{{ n(item.commitment_remaining, 'currency') }}</div>
          </template>

          <template #management_fee="{ item }">
            <div class="text-right text-gray-900">{{ n(item.management_fee, 'currency') }}</div>
          </template>

          <template #other_fees="{ item }">
            <div class="text-right text-gray-900">{{ n(item.other_fees, 'currency') }}</div>
          </template>

          <template #carried_interest_distributed="{ item }">
            <div class="text-right text-gray-900">
              {{ n(item.carried_interest_distributed, 'currency') }}
            </div>
          </template>

          <template #date_established="{ item }">
            <div>
              <div class="text-gray-900">{{ absoluteDate(item.date_established) }}</div>
              <div class="mt-0.5 text-xs text-gray-500">{{ relativeDate(item.date_established) }}</div>
            </div>
          </template>

          <template #payment_status="{ item }">
            <PaymentStatusCell :status="item.payment_status" />
          </template>

          <template #manager="{ item }">
            <span v-if="get(item, 'manager.cid')">
              <RouterLink
                v-if="can('admin', cid)"
                class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
                :to="investorPath(get(item, 'manager.cid'))"
              >
                {{ get(item, 'manager.name') }}
              </RouterLink>
              <span class="text-gray-900" v-else>{{ get(item, 'manager.name') }}</span>
            </span>
          </template>

          <template #managers="{ item }">
            <span v-for="(manager, index) in item.managers ?? []" :key="manager.id">
              <RouterLink
                v-if="can('admin', cid)"
                class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
                :to="{ name: 'investing.individual-overview', params: { individual_id: manager.id } }"
              >
                {{ manager.name }}
              </RouterLink>
              <span class="text-gray-900" v-else>{{ manager.name }}</span>
              <span v-if="index !== (item.managers?.length ?? 0) - 1">, </span>
            </span>
          </template>

          <template #selected_subscriptions="{ item }">
            <div class="flex flex-col px-3 text-left text-sm text-gray-600">
              <a
                v-for="subscription in item.selected_subscriptions ?? []"
                :key="subscription.name"
                class="mb-1 inline-block w-fit whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-300"
                :href="`/${item.group?.subdomain}/billing/${item.entity_type}/${item.id}`"
              >
                <span class="pr-1.5">{{ subscription.name }}</span>
                <span class="border-l border-gray-300 pl-1.5">{{ subscription.status }}</span>
              </a>
            </div>
          </template>
        </VDataTable>
      </div>
    </VSection>
  </TheLayout>
</template>

<style scoped>
.entities-card :deep(.vtable) {
  @apply rounded-none border-0 shadow-none ring-0;
}

/* Collapse the unused accordion/caret column (we don't use sub-rows here).
   --grid-cols is set inline on .entities-card based on visible column count. */
.entities-card :deep(.vtable__inner) {
  grid-template-columns: var(--grid-cols) !important;
}

.entities-card :deep(.vtable__caret_container) {
  padding: 0;
}

/* Roomier column gutters than v-data-table's default px-4. */
.entities-card :deep(.vtable__cell),
.entities-card :deep(.vtable__header_cell) {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

/* Let Status + Name respect their fixed grid tracks instead of growing to fit
   content (default grid min-width: auto). Truncate slots clip the rest. */
.entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(2)),
.entities-card :deep(.vtable__row > .vtable__cell:nth-child(3)),
.entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(3)),
.entities-card :deep(.vtable__row > .vtable__cell:nth-child(4)) {
  min-width: 0;
  overflow: hidden;
}

/* Sticky Status + Name columns only on xl+ (>=1280px). Below that the
   content area can be too narrow for two sticky columns to cohabit with
   the rest of the table, so let everything scroll inline instead. */
@media (min-width: 1280px) {
  /* Status column: header child #2 (after caret), body cell child #3 (after accent + caret). */
  .entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(2)),
  .entities-card :deep(.vtable__row > .vtable__cell:nth-child(3)) {
    position: sticky;
    left: 0;
    z-index: 10;
  }

  .entities-card :deep(.vtable__row > .vtable__cell:nth-child(3)) {
    background-color: white;
  }

  .entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(2)) {
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
  }

  .entities-card :deep(.vtable__row:hover > .vtable__cell:nth-child(3)) {
    background-color: rgb(249 250 251);
  }

  /* Name column: header child #3, body cell child #4. */
  .entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(3)),
  .entities-card :deep(.vtable__row > .vtable__cell:nth-child(4)) {
    position: sticky;
    left: 9rem;
    z-index: 10;
    box-shadow: 1px 0 0 0 rgb(229 231 235);
  }

  .entities-card :deep(.vtable__row > .vtable__cell:nth-child(4)) {
    background-color: white;
  }

  .entities-card :deep(.vtable__header > .vtable__header_cell:nth-child(3)) {
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
  }

  .entities-card :deep(.vtable__row:hover > .vtable__cell:nth-child(4)) {
    background-color: rgb(249 250 251);
  }
}
</style>
